import type { ArticleCarouselContent } from '#types/components/cms/article-carousel'
import type { ArticleGridContent } from '#types/components/cms/article-grid'
import type { SearchData } from '#types/product'

const DEFAULT_ROWS_TO_LOAD = 3

const filtersPerBreakpoint = {
  sm: 4,
  md: 10,
  lg: 20
}

const parseQuery = (query: string): Record<string, string[]> => {
  const urlParams = new URLSearchParams(query)
  return Array.from(urlParams.entries()).reduce((acc, [key, value]) => {
    if (acc[key])
      acc[key] = [...acc[key], value]
    else
      acc[key] = [value]
    return acc
  }, {})
}

export const useArticles = (content: ArticleGridContent | ArticleCarouselContent) => {
  const { $viewport } = useNuxtApp()
  const { getSearch } = useCms()

  const toLoad = content.kind === 'grid'
    ? content.rowsToLoad * content.gridSize.sm! || DEFAULT_ROWS_TO_LOAD
    : content.cardsToLoad

  const rowsToLoad = ref(toLoad)
  const offset = ref(0)
  const expandedFilters = ref(false)
  const pending = ref(true)
  const data = ref<SearchData>()
  const articleRefs = useTemplateRef('articleRefs')

  const queryParsed = content.kind === 'grid' ? parseQuery(content.initialQuery) : undefined
  const queryWithoutFiltersParsed = parseQuery(content.initialQueryNotFiltered)

  const activeFilters = useRouteQuery<string[]>(
    `component${content.id}[tagId]`,
    queryParsed?.tagId || [],
    { transform: (param) => [param].flat() }
  )

  const query = computed(() => ({
    ...(queryParsed || queryWithoutFiltersParsed),
    offset: offset.value,
    limit: rowsToLoad.value,
    tagId: activeFilters.value
  }))

  const fetchArticles = async () => {
    pending.value = true
    try {
      const response = await getSearch('article', query.value) as SearchData
      data.value = {
        ...response,
        result: [
          ...offset.value !== 0 && data.value?.result ? data.value.result : [],
          ...response?.result || []
        ]
      }
    }
    catch (error: any) {
      log.error(`Error fetching articles:`, error)
    }
    finally {
      pending.value = false
    }
  }

  // Show only items up to current loaded amount (pagination-like behavior)
  const articlesToShow = computed(() => {
    const results = data.value?.result || []
    const itemsToShow = offset.value + rowsToLoad.value
    return results.slice(0, itemsToShow)
  })

  const filtersToShow = computed(() => (expandedFilters.value
    ? data.value?.facets
    : data.value?.facets.slice(0, filtersPerBreakpoint[$viewport.breakpoint])) || [])

  const loadMore = async () => {
    const startIndex = articlesToShow.value.length
    offset.value += rowsToLoad.value

    await fetchArticles()
    await nextTick()

    articleRefs.value?.[startIndex]?.focus()
  }

  const clearAllFilters = () => {
    activeFilters.value = queryWithoutFiltersParsed.tagId || []
  }

  const toggleFilter = (filter: string) => {
    if (activeFilters.value.includes(filter))
      activeFilters.value = activeFilters.value.filter((f) => f !== filter)
    else
      activeFilters.value = [...activeFilters.value, filter]
    offset.value = 0
  }

  // Watch activeFilters to reset offset and refetch when filters change
  watch(() => activeFilters.value, () => {
    offset.value = 0
    data.value = undefined
    fetchArticles()
  })

  fetchArticles()

  return {
    data,
    activeFilters,
    expandedFilters,
    filtersPerBreakpoint,
    filtersToShow,
    clearAllFilters,
    toggleFilter,
    articlesToShow,
    pending,
    loadMore,
    articleRefs
  }
}
